import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Activity, Mail, Lock, User, Loader2, ArrowLeft, Phone } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | verify | phone | phone-otp
  const [expiresAt, setExpiresAt] = useState(null);
  const [secsLeft, setSecsLeft] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const [emailSent, setEmailSent] = useState(true);
  const [emailError, setEmailError] = useState(null);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setSecsLeft(Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const applyCodeMeta = (data) => {
    setExpiresAt(data.expires_at || new Date(Date.now() + 5 * 60000).toISOString());
    setCooldown(data.resend_cooldown_seconds || 30);
    setEmailSent(data.email_sent !== false);
    setEmailError(data.email_sent === false ? (data.email_error || null) : null);
  };
  const mmss = (n) => `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devCode, setDevCode] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/phone/request-otp", { phone });
      setDevCode(data.dev_code || "");
      setMode("phone-otp");
      toast.success("OTP generated");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not send OTP");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/phone/verify-otp", { phone, code: otp });
      setUser(data);
      toast.success("Signed in!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Verification failed");
    } finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      setUser(data);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      applyCodeMeta(data);
      if (data.email_sent === false) toast.error("Email could not be delivered — try Resend or continue with Google");
      else toast.success("Verification code sent to your email");
      setCode("");
      setMode("verify");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify", { email: form.email, code });
      setUser(data);
      toast.success("Email verified — trial activated!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setCooldown(30);
    try {
      const { data } = await api.post("/auth/resend-code", { email: form.email });
      applyCodeMeta(data);
      setCode("");
      if (data.email_sent === false) toast.error("Email could not be delivered — check the address or continue with Google");
      else toast.success("New code sent — check your inbox (and spam folder)");
    } catch (err) {
      const retry = /wait (\d+)s/.exec(err.response?.data?.detail || "");
      if (retry) setCooldown(parseInt(retry[1], 10));
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#07090E] text-white flex flex-col">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="relative flex-1 flex items-center justify-center p-4">
        <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-slate-400 hover:text-white text-sm" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="absolute top-6 right-6"><ThemeToggle compact /></div>

        <div className="w-full max-w-md glass rounded-2xl p-8 fade-up">
          <div className="flex items-center gap-2 mb-6">
            <div className="relative">
              <Activity className="w-7 h-7 text-emerald-500" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full pulse-dot" />
            </div>
            <span className="font-display font-extrabold text-lg">AI Trading Copilot</span>
          </div>

          {(mode === "login" || mode === "register") && (
            <>
              <div className="flex gap-2 p-1 bg-white/5 rounded-lg mb-6">
                <button data-testid="tab-login" onClick={() => setMode("login")}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${mode === "login" ? "bg-emerald-500 text-[#07090E]" : "text-slate-400"}`}>
                  Sign In
                </button>
                <button data-testid="tab-register" onClick={() => setMode("register")}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${mode === "register" ? "bg-emerald-500 text-[#07090E]" : "text-slate-400"}`}>
                  Create Account
                </button>
              </div>

              <button onClick={googleLogin} data-testid="google-login-button"
                className="w-full flex items-center justify-center gap-3 bg-white text-slate-900 font-medium py-2.5 rounded-lg hover:bg-slate-100 transition-colors mb-4">
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continue with Google
              </button>
              <button onClick={() => setMode("phone")} data-testid="phone-login-button"
                className="w-full flex items-center justify-center gap-3 bg-white/5 border border-white/10 text-white font-medium py-2.5 rounded-lg hover:bg-white/10 transition-colors mb-4">
                <Phone className="w-4 h-4" /> Continue with phone
              </button>
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-slate-500">OR</span><div className="flex-1 h-px bg-white/10" />
              </div>
            </>
          )}

          {mode === "login" && (
            <form onSubmit={submitLogin} className="space-y-4">
              <Field icon={Mail} type="email" placeholder="Email" value={form.email} onChange={upd("email")} testId="login-email-input" />
              <Field icon={Lock} type="password" placeholder="Password" value={form.password} onChange={upd("password")} testId="login-password-input" />
              <Button type="submit" disabled={loading} data-testid="login-submit-button"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
              </Button>
            </form>
          )}

          {mode === "register" && (
            <form onSubmit={submitRegister} className="space-y-4">
              <p className="text-xs text-slate-500 -mt-2" data-testid="register-hint">Fastest: <button type="button" onClick={googleLogin} className="text-emerald-400 hover:underline">Continue with Google</button> — no verification code needed. Or sign up with email:</p>
              <Field icon={User} type="text" placeholder="Full name" value={form.name} onChange={upd("name")} testId="register-name-input" />
              <Field icon={Mail} type="email" placeholder="Email" value={form.email} onChange={upd("email")} testId="register-email-input" />
              <Field icon={Lock} type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={upd("password")} testId="register-password-input" />
              <Button type="submit" disabled={loading} data-testid="register-submit-button"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account & Start Trial"}
              </Button>
            </form>
          )}

          {mode === "verify" && (
            <form onSubmit={submitVerify} className="space-y-4">
              <p className="text-sm text-slate-400">We sent a 6-digit code to <span className="text-white">{form.email}</span>. Enter it below to activate your trial.</p>
              {!emailSent && (
                <p className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="verify-email-failed">
                  {emailError || "We couldn't deliver the email."} Check the address, try <b>Resend</b>, or use <b>Continue with Google</b> below — no code needed.
                </p>
              )}
              <Input data-testid="verify-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000" maxLength={6} inputMode="numeric" autoFocus
                className="text-center font-mono-num text-2xl tracking-[0.5em] bg-white/5 border-white/10 h-14" />
              <p className={`text-center text-sm font-mono-num ${secsLeft > 60 ? "text-slate-400" : secsLeft > 0 ? "text-amber-400" : "text-red-400"}`} data-testid="verify-countdown" data-seconds={secsLeft}>
                {secsLeft > 0 ? <>Code expires in <span className="text-white font-semibold">{mmss(secsLeft)}</span></> : "Code expired — request a new one below"}
              </p>
              <Button type="submit" disabled={loading || secsLeft === 0 || code.length !== 6} data-testid="verify-submit-button"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Continue"}
              </Button>
              <button type="button" onClick={resend} disabled={cooldown > 0} data-testid="resend-code-button" data-cooldown={cooldown}
                className={`w-full text-sm ${cooldown > 0 ? "text-slate-600 cursor-not-allowed" : "text-slate-400 hover:text-emerald-400"}`}>
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
              <div className="flex items-center gap-3"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-slate-500">OR SKIP THE CODE</span><div className="flex-1 h-px bg-white/10" /></div>
              <button type="button" onClick={googleLogin} data-testid="verify-google-button"
                className="w-full flex items-center justify-center gap-2 bg-white text-slate-900 font-medium py-2.5 rounded-lg hover:bg-slate-100 transition-colors text-sm">
                Continue with Google instead
              </button>
              <button type="button" onClick={() => setMode("register")} className="w-full text-xs text-slate-500 hover:text-white" data-testid="verify-back-button">Wrong email? Go back</button>
            </form>
          )}

          {mode === "phone" && (
            <form onSubmit={requestOtp} className="space-y-4">
              <Field icon={Phone} type="tel" placeholder="Phone (e.g. +91 98123 45678)" value={phone} onChange={(e) => setPhone(e.target.value)} testId="phone-input" />
              <Button type="submit" disabled={loading} data-testid="request-otp-button"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send OTP"}
              </Button>
              <button type="button" onClick={() => setMode("login")} className="w-full text-sm text-slate-400 hover:text-white flex items-center justify-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to sign in
              </button>
            </form>
          )}

          {mode === "phone-otp" && (
            <form onSubmit={verifyOtp} className="space-y-4">
              <p className="text-sm text-slate-400">Enter the 6-digit code for <span className="text-white">{phone}</span>.</p>
              {devCode && (
                <div className="text-xs bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 rounded-lg p-2 text-center" data-testid="dev-otp-hint">
                  SMS not wired yet — your test code is <span className="font-mono-num font-bold">{devCode}</span>
                </div>
              )}
              <Input data-testid="otp-input" value={otp} onChange={(e) => setOtp(e.target.value)}
                placeholder="000000" maxLength={6}
                className="text-center font-mono-num text-2xl tracking-[0.5em] bg-white/5 border-white/10 h-14" />
              <Button type="submit" disabled={loading} data-testid="verify-otp-button"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Continue"}
              </Button>
              <button type="button" onClick={() => setMode("phone")} className="w-full text-sm text-slate-400 hover:text-white flex items-center justify-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Change number
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testId, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
      <Input {...props} data-testid={testId} className="pl-10 bg-white/5 border-white/10 h-11 text-white placeholder:text-slate-500" required />
    </div>
  );
}
